import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { listEmailLog } from "@/lib/admin-console.functions";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/_authenticated/admin/emails")({
  head: () => ({
    meta: [
      { title: "Emails — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EmailsPage,
});

type Row = {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
};

const STATUS_COLOR: Record<string, string> = {
  sent: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  pending: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
  dlq: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  failed: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  bounced: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  complained: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30",
  suppressed: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
};

const RANGES = { "1d": 1, "7d": 7, "30d": 30 } as const;
type Range = keyof typeof RANGES;

function EmailsPage() {
  const fetchLog = useServerFn(listEmailLog);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "emails"],
    queryFn: () => fetchLog(),
    staleTime: 30_000,
  });

  const [range, setRange] = useState<Range>("7d");
  const [template, setTemplate] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [q, setQ] = useState("");

  // Dedup by message_id (latest per message_id)
  const deduped = useMemo<Row[]>(() => {
    const rows = (data?.rows ?? []) as Row[];
    const byMsg = new Map<string, Row>();
    const noMsg: Row[] = [];
    for (const r of rows) {
      if (!r.message_id) {
        noMsg.push(r);
        continue;
      }
      const existing = byMsg.get(r.message_id);
      if (!existing || new Date(r.created_at) > new Date(existing.created_at)) {
        byMsg.set(r.message_id, r);
      }
    }
    return [...byMsg.values(), ...noMsg].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [data]);

  const templates = useMemo(
    () => Array.from(new Set(deduped.map((r) => r.template_name))).sort(),
    [deduped],
  );

  const cutoff = Date.now() - RANGES[range] * 86400_000;
  const filtered = deduped.filter((r) => {
    if (new Date(r.created_at).getTime() < cutoff) return false;
    if (template !== "all" && r.template_name !== template) return false;
    if (status !== "all" && r.status !== status) return false;
    if (q && !r.recipient_email.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: filtered.length,
    sent: filtered.filter((r) => r.status === "sent").length,
    failed: filtered.filter((r) => ["dlq", "failed", "bounced"].includes(r.status)).length,
    suppressed: filtered.filter((r) => r.status === "suppressed").length,
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold">Email Log</h1>
        <p className="text-sm text-muted-foreground">
          Transactional and auth email delivery, deduplicated by message ID.
        </p>
      </header>

      <div className="grid gap-4 grid-cols-2 md:grid-cols-4 mb-6">
        <StatCard label="Total" value={stats.total} />
        <StatCard label="Sent" value={stats.sent} tone="emerald" />
        <StatCard label="Failed" value={stats.failed} tone="rose" />
        <StatCard label="Suppressed" value={stats.suppressed} tone="amber" />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={range} onValueChange={(v) => setRange(v as Range)}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1d">Last 24h</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={template} onValueChange={setTemplate}>
          <SelectTrigger className="w-52"><SelectValue placeholder="Template" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All templates</SelectItem>
            {templates.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="dlq">Failed (dlq)</SelectItem>
            <SelectItem value="suppressed">Suppressed</SelectItem>
            <SelectItem value="bounced">Bounced</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Search recipient email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs ml-auto"
        />
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}

      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-3 font-medium">Template</th>
              <th className="p-3 font-medium">Recipient</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium">Sent</th>
              <th className="p-3 font-medium">Error</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((r) => (
              <tr key={r.id} className="border-t align-top">
                <td className="p-3 font-mono text-xs">{r.template_name}</td>
                <td className="p-3">{r.recipient_email}</td>
                <td className="p-3">
                  <Badge variant="outline" className={STATUS_COLOR[r.status] ?? ""}>
                    {r.status}
                  </Badge>
                </td>
                <td className="p-3 whitespace-nowrap text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="p-3 text-xs text-destructive max-w-md">
                  {r.error_message || "—"}
                </td>
              </tr>
            ))}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-muted-foreground">
                  No emails match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {filtered.length > 200 && (
          <div className="p-3 text-xs text-muted-foreground border-t">
            Showing 200 of {filtered.length} — narrow the filters to see more.
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "emerald" | "rose" | "amber";
}) {
  const toneClass =
    tone === "emerald"
      ? "text-emerald-600"
      : tone === "rose"
        ? "text-rose-600"
        : tone === "amber"
          ? "text-amber-600"
          : "";
  return (
    <div className="rounded-md border p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}
